import React, { useState } from 'react';
import { Select, Modal, Form, Button, Divider, Input, InputNumber, Switch, message, Row, Col } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

export const normalizeText = (text) =>
  (text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const DependentSelect = ({ field, form }) => {
  const depValue = Form.useWatch(field.dependsOn, form);
  const options = depValue && field.getOptions ? field.getOptions(depValue) : [];
  return (
    <Form.Item key={field.name} name={field.name} label={field.label} rules={field.required ? [{ required: true }] : []}>
      <Select
        options={options}
        showSearch
        allowClear
        filterOption={(input, option) => normalizeText(option?.label ?? '').includes(normalizeText(input))}
        placeholder={depValue ? (field.placeholder || `Seleccionar ${field.label}`) : `Seleccione primero ${field.dependsOnLabel || field.dependsOn}`}
        style={{ width: '100%' }}
        disabled={!depValue}
      />
    </Form.Item>
  );
};

const DependentSelectWithCreate = ({ field, form }) => {
  const depValue = Form.useWatch(field.dependsOn, form);
  const baseOptions = depValue && field.getOptions ? field.getOptions(depValue) : [];
  const [extraOptions, setExtraOptions] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const options = [...baseOptions, ...extraOptions.filter(o => !baseOptions.some(b => b.value === o.value))];

  const handleCreate = async (rawValues) => {
    setSaving(true);
    try {
      const submitValues = { ...rawValues, [field.dependsOn]: depValue };
      const result = await field.onCreateSubmit(submitValues);
      setExtraOptions(prev => [...prev, result]);
      field.onCreated?.(result);
      form.setFieldValue(field.name, result.value);
      setCreateOpen(false);
      createForm.resetFields();
      message.success(`${field.createLabel || field.label} creado correctamente`);
    } catch (e) {
      message.error(`Error: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Form.Item key={field.name} name={field.name} label={field.label} rules={field.required ? [{ required: true }] : []}>
        <Select
          options={options}
          showSearch
          allowClear
          filterOption={(input, option) => normalizeText(option?.label ?? '').includes(normalizeText(input))}
          placeholder={depValue ? (field.placeholder || `Seleccionar ${field.label}`) : `Seleccione primero ${field.dependsOnLabel || field.dependsOn}`}
          style={{ width: '100%' }}
          disabled={!depValue}
          dropdownRender={(menu) => (
            <>
              {menu}
              {depValue && (
                <>
                  <Divider style={{ margin: '8px 0' }} />
                  <div
                    style={{ padding: '0 8px 8px', cursor: 'pointer', color: '#1677ff', display: 'flex', alignItems: 'center', gap: 4 }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { createForm.resetFields(); setCreateOpen(true); }}
                  >
                    <PlusOutlined /> {field.createLabel || `Crear ${field.label}`}
                  </div>
                </>
              )}
            </>
          )}
        />
      </Form.Item>
      <Modal
        title={field.createLabel || `Crear ${field.label}`}
        open={createOpen}
        onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate} style={{ marginTop: 16 }}>
          {(field.createFields || []).map(f => (
            <Form.Item key={f.name} name={f.name} label={f.label} rules={f.required ? [{ required: true }] : []}>
              <Input />
            </Form.Item>
          ))}
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={() => { setCreateOpen(false); createForm.resetFields(); }} style={{ marginRight: 8 }}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={saving}>Crear</Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const SelectWithCreate = ({
  value,
  onChange,
  options,
  setOptions,
  createLabel,
  createFields,
  onCreateSubmit,
  ...selectProps
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const handleCreate = async (rawValues) => {
    setSaving(true);
    const values = Object.fromEntries(
      Object.entries(rawValues).map(([k, v]) => [k, v && typeof v === 'object' && typeof v.format === 'function' ? v.format('YYYY-MM-DD') : v])
    );
    try {
      const result = await onCreateSubmit(values);
      if (setOptions && result) {
        setOptions((prev) => [...prev, result]);
      }
      setModalOpen(false);
      form.resetFields();
      if (result?.value !== undefined) {
        onChange?.(result.value);
      }
      message.success(`${createLabel} creado correctamente`);
    } catch (e) {
      message.error(`Error al crear: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field) => {
    if (field.type === 'select') {
      const hasFlagUrls = (field.options || []).some(o => o.flagUrl);
      return (
        <Form.Item key={field.name} name={field.name} label={field.label} rules={field.required ? [{ required: true }] : []}>
          <Select
            options={field.options}
            showSearch
            allowClear={!field.required}
            filterOption={(input, option) =>
              normalizeText(option?.label ?? '').includes(normalizeText(input))
            }
            placeholder={field.placeholder || `Seleccionar ${field.label}`}
            style={{ width: '100%' }}
            optionRender={hasFlagUrls ? (option) => {
              const flagUrl = option.data?.flagUrl ?? option.flagUrl;
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {flagUrl && <img src={flagUrl} alt="" style={{ width: 20, height: 14, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />}
                  <span>{option.label}</span>
                </span>
              );
            } : undefined}
            labelRender={hasFlagUrls ? (props) => {
              const opt = (field.options || []).find(o => o.value === props.value);
              const flagUrl = opt?.flagUrl;
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {flagUrl && <img src={flagUrl} alt="" style={{ width: 20, height: 14, objectFit: 'cover', borderRadius: 2 }} />}
                  <span>{props.label}</span>
                </span>
              );
            } : undefined}
          />
        </Form.Item>
      );
    }
    if (field.type === 'selectWithCreate') {
      return (
        <Form.Item key={field.name} name={field.name} label={field.label} rules={field.required ? [{ required: true }] : []}>
          <SelectWithCreate
            options={field.options || []}
            setOptions={field.setOptions}
            createLabel={field.createLabel}
            createFields={field.createFields}
            onCreateSubmit={field.onCreateSubmit}
            showSearch
            allowClear={!field.required}
            filterOption={(input, option) =>
              normalizeText(option?.label ?? '').includes(normalizeText(input))
            }
            placeholder={field.placeholder || `Seleccionar ${field.label}`}
            style={{ width: '100%' }}
          />
        </Form.Item>
      );
    }
    if (field.type === 'selectFiltered') {
      return <DependentSelect key={field.name} field={field} form={form} />;
    }
    if (field.type === 'selectFilteredWithCreate') {
      return <DependentSelectWithCreate key={field.name} field={field} form={form} />;
    }
    if (field.type === 'selectEscudo') {
      const hasEscudos = (field.options || []).some(o => o.escudoUrl);
      return (
        <Form.Item key={field.name} name={field.name} label={field.label} rules={field.required ? [{ required: true }] : []}>
          <Select
            options={field.options}
            showSearch
            allowClear={!field.required}
            filterOption={(input, option) =>
              normalizeText(option?.label ?? '').includes(normalizeText(input))
            }
            placeholder={field.placeholder || `Seleccionar ${field.label}`}
            style={{ width: '100%' }}
            optionRender={hasEscudos ? (option) => {
              const escudoUrl = option.data?.escudoUrl;
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {escudoUrl
                    ? <img src={escudoUrl} alt="" style={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }} />
                    : <div style={{ width: 22, height: 22, flexShrink: 0 }} />}
                  <span>{option.label}</span>
                </span>
              );
            } : undefined}
            labelRender={hasEscudos ? (props) => {
              const opt = (field.options || []).find(o => o.value === props.value);
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {opt?.escudoUrl && <img src={opt.escudoUrl} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />}
                  <span>{props.label}</span>
                </span>
              );
            } : undefined}
          />
        </Form.Item>
      );
    }
    if (field.type === 'switch') {
      return (
        <Form.Item key={field.name} name={field.name} label={field.label} valuePropName="checked">
          <Switch />
        </Form.Item>
      );
    }
    if (field.type === 'number') {
      return (
        <Form.Item key={field.name} name={field.name} label={field.label} rules={field.required ? [{ required: true }] : []}>
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
      );
    }
    if (field.type === 'date') {
      return (
        <Form.Item key={field.name} name={field.name} label={field.label} rules={field.required ? [{ required: true }] : []}>
          <Input placeholder="YYYY-MM-DD" />
        </Form.Item>
      );
    }
    if (field.type === 'row') {
      return (
        <Row key={field.fields?.map(f => f.name).join('-')} gutter={16}>
          {(field.fields || []).map(f => (
            <Col key={f.name} span={12}>{renderField(f)}</Col>
          ))}
        </Row>
      );
    }
    // default: text input
    return (
      <Form.Item key={field.name} name={field.name} label={field.label} rules={field.required ? [{ required: true }] : []}>
        <Input placeholder={field.placeholder || field.label} />
      </Form.Item>
    );
  };

  return (
    <>
      <Select
        value={value}
        onChange={onChange}
        showSearch
        options={options}
        dropdownRender={(menu) => (
          <>
            {menu}
            <Divider style={{ margin: '8px 0' }} />
            <div
              style={{ padding: '0 8px 8px', cursor: 'pointer', color: '#1677ff', display: 'flex', alignItems: 'center', gap: 4 }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { form.resetFields(); setModalOpen(true); }}
            >
              <PlusOutlined /> {createLabel}
            </div>
          </>
        )}
        {...selectProps}
        filterOption={
          selectProps.filterOption !== undefined
            ? selectProps.filterOption
            : (input, option) => normalizeText(option?.label ?? '').includes(normalizeText(input))
        }
      />
      <Modal
        title={createLabel}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
        destroyOnClose
        width={480}
        zIndex={1200}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate} style={{ marginTop: 16 }}>
          {(createFields || []).map(renderField)}
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={() => { setModalOpen(false); form.resetFields(); }} style={{ marginRight: 8 }}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Crear
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SelectWithCreate;
